import { includes } from 'lodash/fp';
import styled, { css, DefaultTheme } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken, Size, Sized } from '@optimus-web/common';
import {
    TransitionGroupChildBaseProps as DialogChildProps,
    StyledTransitionGroup,
    StyledTransitionGroupProps,
} from '@optimus-web/helpers';
import { ANIMATION_CURVE, ANIMATION_DURATION, MEDIA_QUERY, TransitionState } from '@optimus-web/local-tokens';
import { Button } from '../Button';
import { IconButton, IconButtonProps } from '../IconButton';
import { Stack, StackProps } from '../Stack';
import { Z_INDEX_HIGH } from '../theme';
import { Ellipticable, getEllipticableCss } from '../typography';
import { DialogPosition } from './DialogProps';

interface WithDialogPosition {
    position?: DialogPosition;
}

export const DialogContainerElement = styled(
    (props: Partial<StyledTransitionGroupProps<DialogChildProps>> & { height: number }) => (
        <StyledTransitionGroup {...props} />
    )
)`
    ${({ height }) => css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        inset: 0;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: ${Z_INDEX_HIGH};
        pointer-events: none;
        height: ${height}px;
    `}
`;

export const BackdropElement = styled.div<DialogChildProps>`
    position: absolute;
    inset: 0;

    width: 100%;
    height: 100%;

    opacity: ${getToken(TokenName.OPACITY_0)};
    transition: opacity ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};
    pointer-events: initial;

    background-color: ${getToken(TokenName.BACKGROUND_BACKDROP)};

    ${({ transitionState, theme }) =>
        includes(transitionState, [TransitionState.ENTERING, TransitionState.EXITING]) &&
        css`
            opacity: ${getToken(TokenName.OPACITY_0)({ theme })};
        `}
    ${({ transitionState, theme }) =>
        transitionState === TransitionState.ENTERED &&
        css`
            opacity: ${getToken(TokenName.OPACITY_1000)({ theme })};
        `}
`;

const getDialogMaxWidth = ({
    theme,
    size = 'medium',
    position,
}: {
    theme: DefaultTheme;
    size?: Size;
    position?: DialogPosition;
}) => {
    enum DIALOG_SIZES {
        large = '926px',
        medium = '696px',
        small = '466px',
    }

    return css`
        max-width: calc(
            ${DIALOG_SIZES[size]} -
                ${position === DialogPosition.Aside ? `${getToken(TokenName.SPACING_300)({ theme })}px` : '0px'}
        );
    `;
};

interface DialogElementProps extends DialogChildProps, Sized {
    position?: DialogPosition;
    noMaxWidth?: boolean;
    mobileFullscreen?: boolean;
}

export const DialogElement = styled.div<DialogElementProps>`
    background-color: ${getToken(TokenName.BACKGROUND_STATIC_FLOATING)};
    border-radius: ${getToken(TokenName.BORDER_RADIUS_200)}px;
    ${css`
        border: ${getToken(TokenName.BORDER_WIDTH_100)}px ${getToken(TokenName.BORDER_STYLE_SOLID_STYLE)}
            ${getToken(TokenName.BORDER_STATIC_SECONDARY)};
    `};
    box-shadow: ${getToken(TokenName.SHADOW_300)};
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    z-index: 1;
    width: calc(100% - ${({ theme }) => getToken(TokenName.SPACING_200)({ theme }) * 2}px);
    max-height: calc(100% - ${({ theme }) => getToken(TokenName.SPACING_200)({ theme }) * 2}px);
    ${({ theme, size, position }) => getDialogMaxWidth({ theme, size, position })};
    transition-duration: ${ANIMATION_DURATION.default}ms;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-property: opacity, transform;
    pointer-events: initial;
    ${({ position, theme }) =>
        (position === DialogPosition.Aside || position === DialogPosition.Corner) &&
        css`
            position: absolute;
            right: ${getToken(TokenName.SPACING_300)({ theme })}px;
            bottom: ${getToken(TokenName.SPACING_300)({ theme })}px;
        `}
    ${({ transitionState, theme }) =>
        includes(transitionState, [TransitionState.ENTERING, TransitionState.EXITING]) &&
        css`
            opacity: ${getToken(TokenName.OPACITY_0)({ theme })};
            transform: translateY(-4em);
        `}
    ${({ transitionState, theme }) =>
        transitionState === TransitionState.ENTERED &&
        css`
            opacity: ${getToken(TokenName.OPACITY_1000)({ theme })};
            transform: translateY(0);
        `}
    ${({ noMaxWidth, theme }) =>
        noMaxWidth
            ? css`
                  width: auto;
                  max-width: none;
              `
            : css`
                  ${MEDIA_QUERY['from-medium']} {
                      width: calc(100% - ${getToken(TokenName.SIZING_400)({ theme }) * 2}px);
                      max-height: calc(100vh - ${getToken(TokenName.SPACING_300)({ theme }) * 2}px);
                  }
              `}

    ${({ position, theme, noMaxWidth, mobileFullscreen }) =>
        position === DialogPosition.Aside
            ? css`
                  top: ${getToken(TokenName.SPACING_300)({ theme })}px;

                  ${MEDIA_QUERY['medium-only']} {
                      width: ${noMaxWidth ? 'auto' : `calc(100% - ${getToken(TokenName.SPACING_900)({ theme })}px)`};
                  }

                  ${mobileFullscreenCss}
              `
            : mobileFullscreen &&
              css`
                  ${mobileFullscreenCss}
              `}
`;

export const DialogHeader = styled(Stack)<StackProps & WithDialogPosition>`
    padding: ${getToken(TokenName.SPACING_300)}px;
    gap: ${getToken(TokenName.SPACING_200)}px;
    ${({ position }) =>
        position === DialogPosition.Aside &&
        css`
            ${MEDIA_QUERY['from-medium']} {
                flex-direction: row-reverse;
                justify-content: start;
            }
        `}
`;

export const DialogTitle = styled.h2<Ellipticable & WithDialogPosition>`
    margin: ${getToken(TokenName.SPACING_0)};
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    ${getTextTokenPreset('TITLE_S')};
    ${getEllipticableCss}
`;

export interface DialogContentElementProps {
    position?: DialogPosition;
    withoutPadding?: boolean;
}

export const DialogContentElement = styled.div<DialogContentElementProps>`
    padding: ${getToken(TokenName.SPACING_300)}px;

    ${DialogHeader} + & {
        padding-top: ${getToken(TokenName.SPACING_0)};
    }

    ${({ withoutPadding }) =>
        withoutPadding &&
        css`
            padding-left: ${getToken(TokenName.SPACING_0)};
            padding-right: ${getToken(TokenName.SPACING_0)};
        `}

    display: flex;

    ${({ position }) =>
        position === DialogPosition.Aside &&
        css`
            flex: 1;
        `}

    overflow: auto;
    flex-direction: column;
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    ${getTextTokenPreset('BODY_L')};
`;

export const DialogActionsElement = styled.div`
    padding: ${getToken(TokenName.SPACING_300)}px;
    padding-top: ${getToken(TokenName.SPACING_0)};
    text-align: right;
`;

export const ActionsButtonsStack = styled(Stack)`
    ${MEDIA_QUERY['up-to-extra-small']} {
        width: 100%;
    }
`;

export const DialogFooterWithDetail = styled.div`
    padding: ${getToken(TokenName.SPACING_0)} ${getToken(TokenName.SPACING_300)}px ${getToken(TokenName.SPACING_300)}px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    & > * {
        &:not(:first-child) {
            ${MEDIA_QUERY['from-small']} {
                margin-left: ${getToken(TokenName.SPACING_100)}px;
            }
        }
    }

    ${MEDIA_QUERY['up-to-extra-small']} {
        flex-direction: column;
    }
`;

export const FooterDetailContainer = styled.div`
    ${MEDIA_QUERY['up-to-extra-small']} {
        margin-bottom: ${getToken(TokenName.SPACING_200)}px;
    }
`;

export const DialogCloseButton = styled(IconButton)<IconButtonProps & WithDialogPosition>`
    ${({ position }) =>
        position !== DialogPosition.Aside &&
        css`
            align-self: flex-start;
        `}
`;

export const DialogButtonElement = styled(Button)`
    &:not(:last-child) {
        ${MEDIA_QUERY['up-to-extra-small']} {
            margin-bottom: ${getToken(TokenName.SPACING_200)}px;
        }

        ${MEDIA_QUERY['from-small']} {
            margin-right: ${getToken(TokenName.SPACING_200)}px;
        }
    }

    ${MEDIA_QUERY['up-to-extra-small']} {
        width: 100%;
    }
`;

const mobileFullscreenCss = css`
    ${MEDIA_QUERY['up-to-small']} {
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        max-height: 100%;
        max-width: 100%;
        border-radius: ${getToken(TokenName.BORDER_RADIUS_0)};

        ${DialogContentElement} {
            flex: 1;
        }
    }
`;
